import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import Icon from './Icon';

const SocialShare = ({ futureData, userData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');

  const showMessage = (message) => {
    setPopupMessage(message);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 3000);
  };

  const shareToTwitter = () => {
    const text = `🔮 My AI Future Prediction: ${futureData.score}/100 potential! "${userData.dream}"\n\nDiscover your future with AI Future Simulator! #FuturePrediction #AI`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
    setIsOpen(false);
    showMessage('Shared to Twitter! 🐦');
  };

  const shareToFacebook = () => {
    const text = `Check out my AI-generated future prediction with ${futureData.score}/100 potential!`;
    window.open(`https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(text)}`, '_blank');
    setIsOpen(false);
    showMessage('Shared to Facebook! 📘');
  };

  const copyToClipboard = async () => {
    const text = `🌟 My AI Future Prediction 🌟\n\nName: ${userData.name}\nFuture Potential Score: ${futureData.score}/100\nDream: "${userData.dream}"\nCountry: ${userData.country}\n\nKey Future Events:\n${futureData.timeline.map(event => `• ${event.year}: ${event.title}`).join('\n')}\n\nGenerated by AI Future Simulator - Discover your future!`;
    
    try {
      await navigator.clipboard.writeText(text);
      setIsOpen(false);
      showMessage('Future details copied to clipboard! 📋');
    } catch (err) {
      console.error('Failed to copy: ', err);
      showMessage('Failed to copy to clipboard 😔');
    }
  };

  const downloadAsImage = async () => {
    try {
      showMessage('Creating your future card... ⏳');

      // Create a temporary div with the future card content
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = `
        position: fixed;
        top: -10000px;
        left: -10000px;
        width: 800px;
        height: 600px;
        background: linear-gradient(135deg, #0f0f23, #1a1a2e);
        color: white;
        padding: 40px;
        font-family: 'Inter', sans-serif;
        z-index: 10000;
        border-radius: 20px;
        border: 1px solid rgba(255, 255, 255, 0.1);
      `;

      tempDiv.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="font-size: 36px; font-weight: bold; margin-bottom: 20px; color: white; background: linear-gradient(135deg, #3b82f6, #8b5cf6); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">🌟 AI Future Prediction</h1>
          <h2 style="font-size: 24px; color: #e2e8f0;">${userData.name}'s Future</h2>
        </div>
        
        <div style="text-align: center; margin-bottom: 30px;">
          <div style="font-size: 48px; font-weight: bold; color: ${getScoreColor(futureData.score)}; margin-bottom: 10px;">
            Future Potential: ${futureData.score}/100
          </div>
          <div style="font-size: 20px; color: #e2e8f0; margin-bottom: 10px; font-style: italic;">
            "${userData.dream}"
          </div>
          <div style="font-size: 18px; color: #94a3b8;">
            Based in ${userData.country}
          </div>
        </div>
        
        <div style="margin-top: 40px;">
          <h3 style="font-size: 22px; font-weight: bold; color: white; margin-bottom: 20px; border-bottom: 2px solid #3b82f6; padding-bottom: 10px;">Future Timeline:</h3>
          ${futureData.timeline.slice(0, 3).map(event => `
            <div style="margin-bottom: 15px; display: flex; align-items: center; background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px;">
              <span style="color: #3b82f6; font-weight: bold; margin-right: 15px; font-size: 18px;">${event.year}</span>
              <div>
                <div style="color: #e2e8f0; font-weight: 600; font-size: 16px;">${event.title}</div>
                <div style="color: #94a3b8; font-size: 14px; margin-top: 4px;">${event.milestone} • ${event.probability}% likely</div>
              </div>
            </div>
          `).join('')}
        </div>
        
        <div style="position: absolute; bottom: 30px; left: 0; right: 0; text-align: center; color: #64748b; font-size: 16px;">
          Generated by AI Future Simulator
        </div>
      `;

      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true
      });

      // Clean up
      document.body.removeChild(tempDiv);

      // Convert to image and download
      const image = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `future-prediction-${userData.name.toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = image;
      link.click();
      
      setIsOpen(false);
      showMessage('Future card downloaded! 🎉');
    } catch (error) {
      console.error('Error downloading image:', error);
      showMessage('Failed to download image 😔');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#10b981';
    if (score >= 80) return '#f59e0b';
    if (score >= 70) return '#ef4444';
    return '#6b7280';
  };

  return (
    <div className="social-share-container">
      {/* Success Popup */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            className="success-popup"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Icon name="star" size={16} />
            {popupMessage}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        className="share-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Icon name="share" size={20} />
        Share My Future
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="share-options"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <button onClick={shareToTwitter} className="share-btn twitter">
              <Icon name="twitter" size={16} />
              Twitter
            </button>
            <button onClick={shareToFacebook} className="share-btn facebook">
              <Icon name="facebook" size={16} />
              Facebook
            </button>
            <button onClick={copyToClipboard} className="share-btn copy">
              <Icon name="copy" size={16} />
              Copy Text
            </button>
            <button onClick={downloadAsImage} className="share-btn download">
              <Icon name="download" size={16} />
              Save Image
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SocialShare;